import { createTheme, type Theme } from '@mui/material/styles';
import { brandConfig, type BrandConfig } from '@/config/brand';
import { buildTokens, type BrandTokens, type ThemeMode } from './tokens';
import { adjust, alpha } from './colors';

export type { ThemeMode, BrandTokens };
export { brandConfig };

// Make the derived brand tokens available as `theme.brand` everywhere.
declare module '@mui/material/styles' {
    interface Theme {
        brand: BrandTokens;
    }
    interface ThemeOptions {
        brand?: BrandTokens;
    }
}

/**
 * Cards without an image would otherwise all render the same gradient. Derive a
 * stable hue from the title so each tile stays visually distinct between visits.
 * The card title sits on a dark scrim in both modes, so this stays mid-dark.
 */
export const cardFallbackGradient = (seed: string): string => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    const hue = Math.abs(hash) % 360;
    return `linear-gradient(135deg,
        hsl(${hue} 58% 34%) 0%,
        hsl(${(hue + 42) % 360} 52% 26%) 100%)`;
};

/**
 * Style helpers return `sx` callbacks so they can read the active mode's tokens
 * instead of closing over a single hard-coded palette.
 */
export const styleHelpers = {
    /**
     * `surface` sits on a normal card and tints against the mode's background;
     * `onBrand` sits directly on the brand gradient and tints against that.
     * Using the wrong one makes the box invisible (white on white in light mode).
     */
    iconBox:
        (size: number = 56, on: 'surface' | 'onBrand' = 'surface') =>
        (theme: Theme) => ({
            width: size,
            height: size,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor:
                on === 'onBrand'
                    ? theme.brand.backgrounds.iconBox
                    : theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(15,23,42,0.06)',
            border:
                on === 'onBrand'
                    ? `1px solid ${theme.brand.backgrounds.onBrandBorder}`
                    : theme.brand.borders.hairlineStrong,
            backdropFilter: theme.brand.effects.backdropBlur,
        }),

    roleAvatar: (isAdmin: boolean, size: number = 56) => (theme: Theme) => ({
        width: size,
        height: size,
        fontWeight: 800,
        fontSize: size / 2.9,
        letterSpacing: '0.02em',
        color: '#fff',
        border: theme.brand.borders.hairlineStrong,
        background: isAdmin
            ? 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)'
            : theme.brand.gradients.accent,
        boxShadow: theme.brand.effects.shadowSoft,
    }),

    sectionCard: (mb: number = 3) => (theme: Theme) => ({
        mb,
        borderRadius: 4,
        transition: theme.brand.transitions.smooth,
    }),

    sectionLabel: {
        display: 'block',
        mb: 2,
        color: 'text.secondary',
        fontWeight: 800,
        letterSpacing: '0.14em',
        fontSize: '0.7rem',
    },
};

const semantic = (mode: ThemeMode) =>
    mode === 'dark'
        ? {
              warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
              success: { main: '#22c55e', light: '#4ade80', dark: '#16a34a' },
              error: { main: '#f0616d', light: '#f87171', dark: '#dc2626' },
              info: { main: '#38bdf8', light: '#7dd3fc', dark: '#0284c7' },
          }
        : {
              warning: { main: '#c2740b', light: '#f59e0b', dark: '#92590a' },
              success: { main: '#15803d', light: '#22c55e', dark: '#166534' },
              error: { main: '#c62b39', light: '#ef4444', dark: '#991b1b' },
              info: { main: '#0369a1', light: '#0ea5e9', dark: '#075985' },
          };

export const createAppTheme = (mode: ThemeMode, config: BrandConfig = brandConfig): Theme => {
    const brand = buildTokens(mode, config[mode]);
    const isDark = mode === 'dark';
    const { accent } = brand;
    const hairline = brand.hairlineColor;
    const hairlineStrong = brand.hairlineStrongColor;

    return createTheme({
        brand,
        palette: {
            mode,
            primary: {
                main: accent.primary,
                light: adjust(accent.primary, { dl: 0.1 }),
                dark: adjust(accent.primary, { dl: -0.1 }),
                contrastText: '#fff',
            },
            secondary: {
                main: accent.secondary,
                light: adjust(accent.secondary, { dl: 0.1 }),
                dark: adjust(accent.secondary, { dl: -0.1 }),
            },
            background: {
                default: brand.backgrounds.base,
                paper: brand.backgrounds.raised,
            },
            ...semantic(mode),
            text: {
                primary: brand.text.primary,
                secondary: brand.text.secondary,
            },
            divider: hairline,
        },
        typography: {
            fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            // Tighter tracking at display sizes keeps large headings from looking loose.
            h1: { fontWeight: 800, letterSpacing: '-0.03em' },
            h2: { fontWeight: 800, letterSpacing: '-0.03em' },
            h3: { fontWeight: 800, letterSpacing: '-0.025em' },
            h4: { fontWeight: 800, letterSpacing: '-0.022em' },
            h5: { fontWeight: 750, letterSpacing: '-0.015em' },
            h6: { fontWeight: 700, letterSpacing: '-0.01em' },
            subtitle2: { fontWeight: 600 },
            body2: { lineHeight: 1.6 },
            button: { textTransform: 'none', fontWeight: 650, letterSpacing: 0 },
        },
        shape: { borderRadius: 14 },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: brand.backgrounds.base,
                        backgroundImage: brand.gradients.ambient,
                        backgroundAttachment: 'fixed',
                        backgroundRepeat: 'no-repeat',
                    },
                    '*::selection': { background: alpha(accent.primary, 0.35) },
                    // Keyboard focus must stay obvious in both modes.
                    'a:focus-visible, button:focus-visible, [tabindex]:focus-visible': {
                        outline: `2px solid ${accent.primary}`,
                        outlineOffset: 2,
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        minHeight: 40,
                        borderRadius: 10,
                        paddingLeft: 16,
                        paddingRight: 16,
                        transition: brand.transitions.standard,
                    },
                    sizeSmall: { minHeight: 32, paddingLeft: 10, paddingRight: 10 },
                    contained: {
                        background: brand.gradients.accent,
                        color: '#fff',
                        boxShadow: `0 8px 20px -12px ${alpha(accent.primary, 0.9)}`,
                        '&:hover': {
                            background: brand.gradients.accentHover,
                            boxShadow: `0 12px 26px -12px ${alpha(accent.primary, 1)}`,
                        },
                    },
                    outlined: {
                        borderColor: hairlineStrong,
                        '&:hover': {
                            borderColor: accent.primary,
                            background: alpha(accent.primary, 0.08),
                        },
                    },
                    text: { '&:hover': { background: alpha(accent.primary, 0.1) } },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        transition: brand.transitions.standard,
                        '&:hover': { backgroundColor: alpha(accent.primary, 0.16) },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: brand.backgrounds.raised,
                        borderRadius: 16,
                    },
                    outlined: { borderColor: hairline },
                    elevation1: { boxShadow: brand.effects.shadowSoft },
                    elevation2: { boxShadow: brand.effects.shadowSoft },
                    elevation3: { boxShadow: brand.effects.shadowLift },
                    elevation4: { boxShadow: brand.effects.shadowLift },
                    elevation8: { boxShadow: brand.effects.shadowLift },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 1 },
                styleOverrides: {
                    root: {
                        borderRadius: 18,
                        border: brand.borders.hairline,
                        backgroundImage: brand.gradients.sheen,
                        transition: brand.transitions.smooth,
                    },
                },
            },
            MuiCardContent: {
                styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 600, borderRadius: 8, height: 26 },
                    sizeSmall: { height: 24, fontSize: '0.74rem' },
                    // Scope the tinted treatment per colour. A blanket `filled` rule
                    // would flatten success/warning chips into the primary tint.
                    filled: {
                        '&.MuiChip-colorDefault': {
                            backgroundColor: isDark
                                ? 'rgba(148,163,184,0.16)'
                                : 'rgba(15,23,42,0.06)',
                            border: `1px solid ${hairlineStrong}`,
                            color: brand.text.secondary,
                        },
                        '&.MuiChip-colorPrimary': {
                            backgroundColor: alpha(accent.primary, isDark ? 0.18 : 0.12),
                            border: `1px solid ${alpha(accent.primary, 0.38)}`,
                            color: isDark ? adjust(accent.primary, { l: 0.8 }) : adjust(accent.primary, { l: 0.32 }),
                        },
                        '&.MuiChip-colorSuccess': {
                            backgroundColor: isDark ? 'rgba(34,197,94,0.18)' : 'rgba(22,163,74,0.12)',
                            border: `1px solid ${isDark ? 'rgba(34,197,94,0.38)' : 'rgba(22,163,74,0.34)'}`,
                            color: isDark ? '#BBF7D0' : '#15803d',
                        },
                        '&.MuiChip-colorWarning': {
                            backgroundColor: isDark ? 'rgba(245,158,11,0.18)' : 'rgba(194,116,11,0.12)',
                            border: `1px solid ${isDark ? 'rgba(245,158,11,0.38)' : 'rgba(194,116,11,0.34)'}`,
                            color: isDark ? '#FDE68A' : '#92590a',
                        },
                        '&.MuiChip-colorError': {
                            backgroundColor: isDark ? 'rgba(240,97,109,0.18)' : 'rgba(198,43,57,0.10)',
                            border: `1px solid ${isDark ? 'rgba(240,97,109,0.38)' : 'rgba(198,43,57,0.32)'}`,
                            color: isDark ? '#FECACA' : '#991b1b',
                        },
                    },
                    outlined: { '&.MuiChip-colorDefault': { borderColor: hairlineStrong } },
                    icon: { marginLeft: 6 },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 20,
                        border: brand.borders.hairlineStrong,
                        backgroundColor: brand.backgrounds.raisedHigh,
                        backgroundImage: brand.gradients.sheen,
                        boxShadow: isDark
                            ? '0 40px 80px -24px rgba(0,0,0,0.85)'
                            : '0 40px 80px -28px rgba(16,24,40,0.35)',
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontSize: '1.35rem',
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        padding: '24px 24px 8px',
                    },
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        '&::-webkit-scrollbar': { width: 10 },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: isDark ? 'rgba(148,163,184,0.28)' : 'rgba(15,23,42,0.22)',
                            borderRadius: 8,
                            border: '3px solid transparent',
                            backgroundClip: 'content-box',
                        },
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: { root: { padding: 20, borderTop: brand.borders.hairline, gap: 8 } },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 10,
                            backgroundColor: isDark ? 'rgba(8,12,22,0.55)' : 'rgba(15,23,42,0.03)',
                            '& fieldset': { borderColor: hairline },
                            '&:hover fieldset': { borderColor: hairlineStrong },
                            '&.Mui-focused fieldset': { borderColor: accent.primary, borderWidth: 2 },
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: hairline, paddingTop: 14, paddingBottom: 14 },
                    head: {
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: brand.text.secondary,
                        backgroundColor: 'transparent',
                        borderBottom: `1px solid ${hairlineStrong}`,
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transition: brand.transitions.standard,
                        '&:hover': { backgroundColor: alpha(accent.primary, isDark ? 0.06 : 0.05) },
                        '&:last-child td': { borderBottom: 0 },
                    },
                },
            },
            MuiAvatar: { styleOverrides: { root: { fontWeight: 800 } } },
            MuiBadge: { styleOverrides: { badge: { fontWeight: 700, fontSize: '0.75rem' } } },
            MuiFormControlLabel: { styleOverrides: { root: { marginLeft: 0, marginRight: 0 } } },
            MuiCheckbox: { styleOverrides: { root: { '&.Mui-checked': { color: accent.primary } } } },
            MuiStack: { defaultProps: { useFlexGap: true } },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: isDark ? 'rgba(8,12,22,0.96)' : 'rgba(16,24,40,0.94)',
                        backdropFilter: brand.effects.backdropBlur,
                        color: '#fff',
                        fontSize: '0.8rem',
                        fontWeight: 550,
                        padding: '7px 11px',
                        borderRadius: 8,
                    },
                    arrow: { color: isDark ? 'rgba(8,12,22,0.96)' : 'rgba(16,24,40,0.94)' },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: hairline },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: hairlineStrong },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: accent.primary,
                            borderWidth: 2,
                        },
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12,
                        border: brand.borders.hairline,
                        backgroundColor: brand.backgrounds.raisedHigh,
                    },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: { padding: 8 },
                    track: {
                        borderRadius: 11,
                        backgroundColor: isDark ? 'rgba(148,163,184,0.35)' : 'rgba(15,23,42,0.25)',
                    },
                    thumb: { boxShadow: '0 2px 4px rgba(0,0,0,0.25)' },
                    switchBase: {
                        '&.Mui-checked': {
                            color: accent.primary,
                            '& + .MuiSwitch-track': {
                                backgroundColor: accent.primary,
                                opacity: 0.65,
                            },
                        },
                    },
                },
            },
            MuiAlert: { styleOverrides: { root: { borderRadius: 12, border: brand.borders.hairline } } },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: 999,
                        height: 6,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
                    },
                    bar: { borderRadius: 999, background: brand.gradients.accent },
                },
            },
        },
    });
};
